// Card 53, here be monsters (R2637, ui-map-interface.md §17.3.53,
// v2-the-deep.md §8.3, §13; mockup round 031 card 53 and round 088 card 03).
//
// The old chart set a sea beast where the sea was not known, with the words
// beside it. While the picture holds a monster of the NPC side, the words
// HERE BE MONSTERS stand under its ragged ring, which the fleet layer draws.
// When the picture loses the monster, a mark stands at the last sighting
// with the world time of it: the biologics tag of the sonar room (a hollow
// diamond and BIO) in the modern reading, a sea serpent and "hic sunt
// dracones" in the historical one. A sighting stands for `life_world_sec`,
// and a new sighting of the same monster moves it.
//
// **IT DRAWS WHAT THE WIRE SENDS** (v2 §14.1 rule 5). A monster hull
// reaches the wire only while the flag `pirates` is latched, so a world
// with the flag off, the arcade and a film draw nothing here.
//
// **COST.** A live piece: one walk of the hulls a frame, and at most
// `max_marks` sightings, the newest kept (§10).

import { NpcKind } from '../../../gen/proto/aquaphobia';
import { NPC_INK, type CHART } from '../../../gen/presentation';
import { chart as S } from '../../../gen/strings';
import type { FrameInfo } from '../../layer';
import type { SegmentSink } from '../../layers/batches';
import type { ChartContext, ChartSinks, Piece } from '../context';
import { hhmm } from '../../../locale/clock';
import { faceOf, inkOf } from './shared';

type Fig = typeof CHART.pieces.here_be_monsters.fig;

/** Where a monster was drawn last. */
export interface Sighting {
  id: number;
  x: number;
  y: number;
  /** The world clock of the sighting, in world seconds. */
  atS: number;
  /** The picture holds the monster in this frame. */
  held: boolean;
  /** The reveal fade of the hull in the last frame that held it. */
  alpha: number;
}

/** The hollow diamond of the biologics tag, of half-size `s`. */
function diamond(seg: SegmentSink, x: number, y: number, s: number, w: number, c: number, a: number): void {
  seg.line(x, y - s, x + s, y, w, c, a);
  seg.line(x + s, y, x, y + s, w, c, a);
  seg.line(x, y + s, x - s, y, w, c, a);
  seg.line(x - s, y, x, y - s, w, c, a);
}

/** The sea serpent of the historical reading: a wave `serpent_px` long. */
function serpent(seg: SegmentSink, x: number, y: number, f: Fig, w: number, c: number, a: number): void {
  const steps = f.serpent_steps, len = f.serpent_px;
  let px = x - len / 2, py = y;
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const qx = x - len / 2 + t * len, qy = y + Math.sin(t * Math.PI * f.serpent_waves) * (len / f.serpent_amp_div);
    seg.line(px, py, qx, qy, w, c, a);
    px = qx; py = qy;
  }
}

export class HereBeMonstersPiece implements Piece {
  readonly id = 'here_be_monsters' as const;
  readonly tier = 'screen' as const;
  readonly pack = 'live' as const;
  /** A sighting belongs to the picture of today: the film hides it. */
  readonly film = 'hide' as const;
  readonly rebuildOn = ['hello'] as const;

  /** The sightings by fleet id. The tests read them. */
  readonly sightings = new Map<number, Sighting>();

  visible(ctx: ChartContext): boolean {
    return ctx.scene.fleets != null;
  }

  /** A new world forgets every sighting. */
  rebuild(_ctx: ChartContext): void {
    this.sightings.clear();
  }

  update(ctx: ChartContext, _frame: FrameInfo): void {
    const layer = ctx.scene.fleets;
    for (const s of this.sightings.values()) s.held = false;
    if (layer) {
      for (const f of layer.chartFleets()) {
        if (f.npc !== NpcKind.NPC_KIND_MONSTER) continue;
        const s = this.sightings.get(f.id);
        if (s) { s.x = f.x; s.y = f.y; s.atS = ctx.worldClockS; s.held = true; s.alpha = f.alpha; }
        else this.sightings.set(f.id, { id: f.id, x: f.x, y: f.y, atS: ctx.worldClockS, held: true, alpha: f.alpha });
      }
    }
    this.prune(ctx);
  }

  /** Drops a sighting past its life, then the oldest past `max_marks`. */
  private prune(ctx: ChartContext): void {
    const f = ctx.cfg.pieces.here_be_monsters.fig;
    for (const [id, s] of this.sightings) {
      if (!s.held && ctx.worldClockS - s.atS > f.life_world_sec) this.sightings.delete(id);
    }
    while (this.sightings.size > f.max_marks) {
      let oldest: Sighting | null = null;
      for (const s of this.sightings.values()) if (!oldest || s.atS < oldest.atS) oldest = s;
      if (!oldest) break;
      this.sightings.delete(oldest.id);
    }
  }

  emit(sinks: ChartSinks, ctx: ChartContext, _frame: FrameInfo): void {
    if (this.sightings.size === 0) return;
    const t = ctx.cfg.pieces.here_be_monsters;
    const r = t[ctx.reading];
    const hist = ctx.reading === 'historical';
    const ink = inkOf(r.ink, NPC_INK.color);
    const face = faceOf(ctx.reading);
    const a = t.fig.alpha * r.alpha;
    for (const s of this.sightings.values()) {
      if (s.held) {
        ctx.glyphs.word(sinks.quads, hist ? S.monsters_historical : S.monsters_modern,
          s.x, s.y + t.fig.label_dy, face, t.text.main, ink, r.alpha * s.alpha, { anchor: 'centre' });
        continue;
      }
      if (hist) {
        serpent(sinks.segments, s.x, s.y, t.fig, r.width, ink, a);
        sinks.circles.disc(s.x + t.fig.serpent_px / 2 + t.fig.head_dx, s.y + t.fig.head_dy, t.fig.head_px, ink, a);
        ctx.glyphs.word(sinks.quads, S.monster_last_historical, s.x, s.y + t.fig.label_dy * t.fig.last_label_k,
          face, t.text.sighting, ink, r.alpha, { anchor: 'centre' });
        continue;
      }
      diamond(sinks.segments, s.x, s.y, t.fig.mark_px, r.width, ink, a);
      ctx.glyphs.word(sinks.quads, S.monster_bio, s.x, s.y, face, t.text.sighting, ink, r.alpha, { anchor: 'centre' });
      ctx.glyphs.word(sinks.quads, S.monster_last({ time: hhmm(s.atS) }), s.x + t.fig.tag_dx, s.y + t.fig.tag_dy,
        face, t.text.sighting, ink, r.alpha);
    }
  }
}

/** The one instance the registry holds. */
export const hereBeMonsters = new HereBeMonstersPiece();
